import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    if (!isHome) return;
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-xl shadow-sm border-b border-sky-100' : 'bg-white/80 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 flex-shrink-0">
            <img
              src="/logo.png"
              alt="Clarendon Family Medicine"
              className="h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {isHome ? (
              <>
                <button onClick={() => scrollTo('services')} className="text-sky-800 hover:text-sky-600 font-medium transition-colors duration-200 text-sm uppercase tracking-wide">Services</button>
                <button onClick={() => scrollTo('about')} className="text-sky-800 hover:text-sky-600 font-medium transition-colors duration-200 text-sm uppercase tracking-wide">About</button>
                <button onClick={() => scrollTo('hours')} className="text-sky-800 hover:text-sky-600 font-medium transition-colors duration-200 text-sm uppercase tracking-wide">Hours</button>
                <button onClick={() => scrollTo('contact')} className="text-sky-800 hover:text-sky-600 font-medium transition-colors duration-200 text-sm uppercase tracking-wide">Contact</button>
              </>
            ) : (
              <Link to="/" className="text-sky-800 hover:text-sky-600 font-medium transition-colors duration-200 text-sm uppercase tracking-wide">Home</Link>
            )}
            <a
              href="tel:8068743531"
              data-testid="nav-call-btn"
              className="flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-200 hover:shadow-lg hover:shadow-sky-200"
            >
              <Phone size={15} />
              (806) 874-3531
            </a>
          </nav>

          {/* Mobile menu button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 text-sky-800 hover:text-sky-600"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div data-testid="mobile-menu" className="md:hidden bg-white border-t border-sky-100 px-6 py-4 space-y-3 shadow-lg">
          {isHome ? (
            <>
              <button onClick={() => scrollTo('services')} className="block w-full text-left py-2 text-sky-800 font-medium">Services</button>
              <button onClick={() => scrollTo('about')} className="block w-full text-left py-2 text-sky-800 font-medium">About</button>
              <button onClick={() => scrollTo('hours')} className="block w-full text-left py-2 text-sky-800 font-medium">Hours</button>
              <button onClick={() => scrollTo('contact')} className="block w-full text-left py-2 text-sky-800 font-medium">Contact</button>
            </>
          ) : (
            <Link to="/" className="block py-2 text-sky-800 font-medium" onClick={() => setMenuOpen(false)}>Home</Link>
          )}
          <a
            href="tel:8068743531"
            className="flex items-center gap-2 bg-sky-600 text-white px-5 py-3 rounded-full font-semibold text-sm w-full justify-center"
          >
            <Phone size={15} />
            Call Us: (806) 874-3531
          </a>
        </div>
      )}
    </header>
  );
}
